import type {
  Candidato,
  ConsultaDeCep,
  ConsultaDeCnpj,
  CandidatoEntrada,
  CandidatoPatch,
  Cobranca,
  CriarCobranca,
  CriarOperador,
  Documento,
  ErroApi,
  EtapaId,
  OperadorDaPlataforma,
  Papel,
  PreferenciasDoOperador,
  PreferenciasLidas,
  ResumoDoPainel,
  TipoDocumento,
} from '@aponte/shared';
import { tokenAtual } from '../autenticacao/cognito.js';
import { configuracao } from '../configuracao.js';

/**
 * Cliente da API.
 *
 * Toda requisição leva o id token do Cognito. O API Gateway valida a assinatura antes de
 * chegar na Lambda, então um token expirado volta 401 — nesse caso recarregamos a página
 * para o fluxo de login assumir.
 */
export class ErroDaApi extends Error {
  readonly codigo: string;
  readonly campos: Record<string, string> | undefined;
  readonly status: number;

  constructor(status: number, corpo: ErroApi) {
    super(corpo.mensagem);
    this.name = 'ErroDaApi';
    this.status = status;
    this.codigo = corpo.codigo;
    this.campos = corpo.campos;
  }
}

async function chamar<T>(
  caminho: string,
  opcoes: { metodo?: string; corpo?: unknown; versaoEsperada?: string } = {},
): Promise<T> {
  const token = await tokenAtual();
  if (!token) {
    throw new ErroDaApi(401, { codigo: 'nao_autenticado', mensagem: 'Sessão expirada.' });
  }

  let resposta: Response;
  try {
    resposta = await fetch(`${configuracao.urlDaApi}${caminho}`, {
      method: opcoes.metodo ?? 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(opcoes.corpo ? { 'Content-Type': 'application/json' } : {}),
        // Trava otimista: o servidor recusa se a ficha mudou desde que a carregamos.
        ...(opcoes.versaoEsperada ? { 'If-Match': `"${opcoes.versaoEsperada}"` } : {}),
      },
      body: opcoes.corpo ? JSON.stringify(opcoes.corpo) : undefined,
    });
  } catch {
    // `fetch` só rejeita por falha de rede ou bloqueio de CORS. Nos dois casos o
    // navegador não conta qual foi — "Failed to fetch" não ajuda ninguém na tela.
    throw new ErroDaApi(0, {
      codigo: 'sem_conexao',
      mensagem:
        'Não foi possível falar com o servidor. Verifique sua conexão e tente de novo; ' +
        'se persistir, avise a operação.',
    });
  }

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  const dados = texto ? JSON.parse(texto) : {};

  if (!resposta.ok) {
    throw new ErroDaApi(resposta.status, {
      codigo: dados.codigo ?? 'interno',
      mensagem: dados.mensagem ?? `Falha na requisição (HTTP ${resposta.status}).`,
      campos: dados.campos,
    });
  }

  return dados as T;
}

/** Documento com o aviso de divergência calculado pelo servidor. */
export interface DocumentoNaFicha extends Documento {
  desatualizado?: boolean;
}

export interface FichaCompleta {
  candidato: Candidato;
  cobrancas: Cobranca[];
  documentos: DocumentoNaFicha[];
  eventos: EventoDaFicha[];
}

export interface EventoDaFicha {
  eventoId: string;
  tipo: string;
  resumo: string;
  autor: string;
  ocorridoEm: string;
}

export const api = {
  painel: () => chamar<ResumoDoPainel>('/painel'),

  listarCandidatos: () =>
    chamar<{ candidatos: Candidato[] }>('/candidatos').then((r) => r.candidatos),

  obterFicha: (id: string) => chamar<FichaCompleta>(`/candidatos/${id}`),

  criarCandidato: (entrada: Partial<CandidatoEntrada>) =>
    chamar<Candidato>('/candidatos', { metodo: 'POST', corpo: entrada }),

  atualizarCandidato: (id: string, patch: CandidatoPatch, versaoEsperada?: string) =>
    chamar<Candidato>(`/candidatos/${id}`, { metodo: 'PATCH', corpo: patch, versaoEsperada }),

  excluirCandidato: (id: string) => chamar<void>(`/candidatos/${id}`, { metodo: 'DELETE' }),

  mudarEtapa: (id: string, etapa: EtapaId, motivo?: string) =>
    chamar<Candidato>(`/candidatos/${id}/etapa`, { metodo: 'POST', corpo: { etapa, motivo } }),

  emitirDocumento: (id: string, tipo: TipoDocumento) =>
    chamar<{ documento: Documento; url: string }>(`/candidatos/${id}/documentos`, {
      metodo: 'POST',
      corpo: { tipo },
    }),

  urlDoDocumento: (id: string, documentoId: string) =>
    chamar<{ url: string }>(`/candidatos/${id}/documentos/${documentoId}/url`),

  gerarCobranca: (id: string, pedido: CriarCobranca) =>
    chamar<Cobranca>(`/candidatos/${id}/cobrancas`, { metodo: 'POST', corpo: pedido }),

  /** Pede a URL temporária, envia o PDF assinado direto ao S3 e confirma o recebimento. */
  registrarAssinatura: async (
    id: string,
    documentoId: string,
    arquivo: File | null,
    motivo?: string,
  ) => {
    let chaveS3Assinado: string | undefined;

    if (arquivo) {
      const { url, chaveS3 } = await chamar<{ url: string; chaveS3: string }>(
        `/candidatos/${id}/documentos/${documentoId}/upload-assinado`,
        { metodo: 'POST' },
      );

      const envio = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/pdf', 'x-amz-server-side-encryption': 'AES256' },
        body: arquivo,
      });
      if (!envio.ok) {
        throw new ErroDaApi(envio.status, {
          codigo: 'upload_falhou',
          mensagem: 'Não foi possível enviar o PDF assinado. Tente novamente.',
        });
      }
      chaveS3Assinado = chaveS3;
    }

    return chamar<{ documento: Documento }>(
      `/candidatos/${id}/documentos/${documentoId}/assinatura`,
      { metodo: 'POST', corpo: { chaveS3Assinado, motivo } },
    );
  },

  /** Auxílio de digitação: o CEP preenche o endereço. */
  consultarCep: (cep: string) => chamar<ConsultaDeCep>(`/consultas/cep/${cep}`),

  /** Traz razão social, endereço e telefone da base pública da Receita. */
  consultarCnpj: (cnpj: string) => chamar<ConsultaDeCnpj>(`/consultas/cnpj/${cnpj}`),

  // ─── Operadores da plataforma (admin) ──────────────────────────────────────
  listarOperadores: () =>
    chamar<{ operadores: OperadorDaPlataforma[] }>('/operadores').then((r) => r.operadores),

  criarOperador: (entrada: CriarOperador) =>
    chamar<OperadorDaPlataforma>('/operadores', { metodo: 'POST', corpo: entrada }),

  definirPapeis: (id: string, papeis: Papel[]) =>
    chamar<OperadorDaPlataforma>(`/operadores/${id}/papeis`, { metodo: 'PUT', corpo: { papeis } }),

  definirSituacao: (id: string, ativo: boolean, motivo: string) =>
    chamar<OperadorDaPlataforma>(`/operadores/${id}/situacao`, {
      metodo: 'POST',
      corpo: { ativo, motivo },
    }),

  reenviarConvite: (id: string) =>
    chamar<OperadorDaPlataforma>(`/operadores/${id}/convite`, { metodo: 'POST' }),

  reiniciarMfa: (id: string) =>
    chamar<OperadorDaPlataforma>(`/operadores/${id}/mfa`, { metodo: 'POST' }),

  // ─── Preferências de quem está logado ──────────────────────────────────────
  /** Sem id na URL: o dono é sempre o `sub` do token. */
  obterPreferencias: () => chamar<PreferenciasLidas>('/preferencias'),

  salvarPreferencias: (preferencias: PreferenciasDoOperador) =>
    chamar<PreferenciasLidas>('/preferencias', { metodo: 'PUT', corpo: preferencias }),

  abrirOuFecharPendencia: (id: string, pendencia: string | null, motivo: string) =>
    chamar<Candidato>(`/candidatos/${id}/pendencia-financeira`, {
      metodo: 'POST',
      corpo: { pendencia, motivo },
    }),
};
